// NOTE: このファイルはまだちゃんとレビューされていません。コードの書き方を真似しないでください。
import styles from "../TaigaPage.module.scss"

const HOUR_MS = 60 * 60 * 1000
const DAY_MS = 24 * HOUR_MS

// 各ボタンの選択肢値 (null = 「全部」/「全て」= 選択解除)
export type PriorityFilterValue = 0 | 1 | 2 | "3plus" | null
export type DueDateFilterValue =
	| "today_or_before"
	| 1
	| 2
	| 3
	| 4
	| "5plus"
	| null
// 複数選択で保持する実際の選択値 (null は持たない。空配列 = 全部)
export type PrioritySelectableValue = Exclude<PriorityFilterValue, null>
export type DueDateSelectableValue = Exclude<DueDateFilterValue, null>
// 送信者フィルタ。複数選択で持つ。
// - null: 全送信者を選択している状態 (デフォルト)。後から増えた送信者も自動で含む。
// - string[]: 明示的に選択した送信者名の配列 (空配列 = 誰も選択していない = 0 件表示)。
export type SenderFilterValue = string[] | null

// RC (Remote Control) フィルタ。3値の排他選択で持つ。
// - all: フィルタ無効 (RC の有無を問わず全件表示)。デフォルト。
// - rc: Remote Control 有効セッションを持つメンションだけ表示する。
// - nonRc: Remote Control 有効セッションを持つメンションを除外する。
export type RcFilterValue = "all" | "rc" | "nonRc"
export const RC_FILTER_OPTIONS: { label: string; value: RcFilterValue }[] = [
	{ label: "すべて", value: "all" },
	{ label: "RC", value: "rc" },
	{ label: "RC外", value: "nonRc" },
]

// セッション(作業状態)フィルタ。busy/idle の独立トグル + RC の3値選択で持つ。
// セッションノードの status (BUSY / それ以外 = IDLE・PENDING) で判定する。
// - busy: BUSY 状態のセッションノードを持つメンションを表示するか。
// - idle: BUSY 以外 (IDLE / PENDING) のセッションノードを持つメンションを表示するか。
// - rc: RC フィルタ (all / rc / nonRc)。busy/idle の絞り込みとは独立に効く。
// デフォルトは busy/idle が false・rc が "all" (= フィルタ無効 = 全件表示)。
// busy/idle のどちらか/両方 ON のときはセッションノードを持つメンションだけに絞り込み、
// 両方 OFF のときはセッションノードを持たないメンションも含めて全件表示する。
export type SessionFilter = {
	busy: boolean
	idle: boolean
	rc: RcFilterValue
}
export const DEFAULT_SESSION_FILTER: SessionFilter = {
	busy: false,
	idle: false,
	rc: "all",
}

export const PRIORITY_FILTER_OPTIONS: {
	label: string
	value: PriorityFilterValue
}[] = [
	{ label: "全部", value: null },
	{ label: "0", value: 0 },
	{ label: "1", value: 1 },
	{ label: "2", value: 2 },
	{ label: "3+", value: "3plus" },
]

export const DUE_DATE_FILTER_OPTIONS: {
	label: string
	value: DueDateFilterValue
}[] = [
	{ label: "全て", value: null },
	{ label: "今日以前", value: "today_or_before" },
	{ label: "1日後", value: 1 },
	{ label: "2", value: 2 },
	{ label: "3", value: 3 },
	{ label: "4", value: 4 },
	{ label: "5+", value: "5plus" },
]

export const cx = (...classes: (string | false | null | undefined)[]) =>
	classes.filter(Boolean).join(" ")

const getElapsedMs = (value?: string | null) => {
	if (!value) return 0
	const time = new Date(value).getTime()
	if (Number.isNaN(time)) return 0
	return Math.max(0, Date.now() - time)
}

const getElapsedStage = (elapsedMs: number) => {
	if (elapsedMs < 2 * HOUR_MS) return 1
	if (elapsedMs < 8 * HOUR_MS) return 2
	if (elapsedMs < DAY_MS) return 3
	if (elapsedMs < 3 * DAY_MS) return 4
	if (elapsedMs < 7 * DAY_MS) return 5
	return 6
}

export const getAgeClassName = (value?: string | null) => {
	switch (getElapsedStage(getElapsedMs(value))) {
		case 1:
			return styles.deferNormal
		case 2:
			return styles.deferWarnLight
		case 3:
			return styles.deferWarnStrong
		case 4:
			return styles.deferDangerLight
		case 5:
			return styles.deferDangerStrong
		default:
			return styles.deferCritical
	}
}

/**
 * 「今日(JST)」を YYYY-MM-DD 文字列で返す。
 */
const getJstTodayString = (): string => {
	return new Date().toLocaleDateString("sv-SE", { timeZone: "Asia/Tokyo" })
}

/**
 * due_date (YYYY-MM-DD) から今日(JST)までの残り日数を返す。
 * - 正の値: 期限が未来（例: 3 = 3日後）
 * - 0: 今日が期限
 * - 負の値: 期限超過（例: -2 = 2日超過）
 */
export const getRemainingDays = (dueDate: string): number => {
	const today = getJstTodayString()
	const todayMs = new Date(`${today}T00:00:00`).getTime()
	const dueDateMs = new Date(`${dueDate}T00:00:00`).getTime()
	return Math.round((dueDateMs - todayMs) / DAY_MS)
}

/**
 * due_date に基づくバッジ背景色のクラス名を返す。
 * 残り日数が少ない/超過しているほど強い色。
 * - 超過 (< 0): critical
 * - 今日 (= 0): dangerStrong
 * - 明日 (= 1): dangerLight
 * - 2日後: warnStrong
 * - 3日後: warnLight
 * - 4日後以上: normal
 */
export const getDueDateBadgeClassName = (dueDate: string) => {
	const remaining = getRemainingDays(dueDate)
	if (remaining < 0) return styles.deferredCritical
	if (remaining === 0) return styles.deferredDangerStrong
	if (remaining === 1) return styles.deferredDangerLight
	if (remaining === 2) return styles.deferredWarnStrong
	if (remaining === 3) return styles.deferredWarnLight
	return styles.deferredNormal
}

export const getPriorityBadgeClassName = (priority: number) => {
	if (priority <= 0) return styles.priorityBadgeUrgent
	if (priority === 1) return styles.priorityBadgeHigh
	if (priority === 2) return styles.priorityBadgeNormal
	return styles.priorityBadgeLow
}

// 単一の選択肢値にマッチするか (件数表示の集計用)
export const matchesPriorityValue = (
	priority: number,
	value: PrioritySelectableValue
) => {
	if (value === "3plus") return priority >= 3
	return priority === value
}

// 複数選択フィルタ: 空なら全件、いずれかにマッチすれば true (OR)
export const matchesPriorityFilter = (
	priority: number,
	filter: PrioritySelectableValue[]
) => {
	if (filter.length === 0) return true
	return filter.some((value) => matchesPriorityValue(priority, value))
}

// 単一の選択肢値にマッチするか (件数表示の集計用)
export const matchesDueDateValue = (
	dueDate: string,
	value: DueDateSelectableValue
) => {
	const remaining = getRemainingDays(dueDate)
	if (value === "today_or_before") return remaining <= 0
	if (value === "5plus") return remaining >= 5
	return remaining === value
}

// 複数選択フィルタ: 空なら全件、いずれかにマッチすれば true (OR)
export const matchesDueDateFilter = (
	dueDate: string,
	filter: DueDateSelectableValue[]
) => {
	if (filter.length === 0) return true
	return filter.some((value) => matchesDueDateValue(dueDate, value))
}

// 送信者の複数選択フィルタ。null (全送信者) なら全件通す。
// 配列のときは選択された送信者名に一致するものだけ通す
// (送信者名が無いメンションは、明示選択中は通さない)。
export const matchesSenderFilter = (
	senderDisplayName: string | null | undefined,
	filter: SenderFilterValue
) => {
	if (filter === null) return true
	if (!senderDisplayName) return false
	return filter.includes(senderDisplayName)
}

// busy/idle トグルの状態に応じてメンションを表示するか判定する。
// - 両方 OFF: フィルタ無効 = 全件表示 (セッションノードを持たないメンションも含む)。
// - どちらか/両方 ON: ON にしたバケットのセッションノードを持つメンションだけ表示する。
//   busy ON → busy セッションノードを持つ / idle ON → idle セッションノードを持つ /
//   両方 ON → busy か idle どちらかのセッションノードを持つ。
//   セッションノードを持たないメンションは (ON が1つでもあれば) 表示されない。
export const matchesSessionFilter = (
	hasBusy: boolean,
	hasIdle: boolean,
	hasRc: boolean,
	filter: SessionFilter
) => {
	// RC フィルタは busy/idle の絞り込みとは独立に適用する
	if (filter.rc === "rc" && !hasRc) return false
	if (filter.rc === "nonRc" && hasRc) return false
	if (!filter.busy && !filter.idle) return true
	return (filter.busy && hasBusy) || (filter.idle && hasIdle)
}

// メモのキーワード検索。空文字 = 検索なし (全件表示)。
export type SearchFilterValue = string

// メモ (mention のメモ / node のセッションメモ) をキーワードで部分一致検索する。
// - keyword が空 (空白のみ含む) なら全件通す。
// - 空白区切りで複数語を書いた場合は AND 条件 (全部含むものだけ残す)。
// - 大文字小文字は区別しない。
export const matchesSearchFilter = (
	targets: (string | null | undefined)[],
	keyword: SearchFilterValue
) => {
	const words = keyword.toLowerCase().split(/\s+/).filter(Boolean)
	if (words.length === 0) return true
	const haystack = targets
		.filter((text): text is string => Boolean(text))
		.join("\n")
		.toLowerCase()
	return words.every((word) => haystack.includes(word))
}

export const formatRelativeTime = (value: string) => {
	const elapsedMs = getElapsedMs(value)
	if (elapsedMs < 60 * 1000) return "now"
	if (elapsedMs < HOUR_MS) return `${Math.floor(elapsedMs / 60000)}m ago`
	if (elapsedMs < DAY_MS) return `${Math.floor(elapsedMs / HOUR_MS)}h ago`
	return `${Math.floor(elapsedMs / DAY_MS)}d ago`
}

export const formatElapsedCompact = (value: string) => {
	const elapsedMs = getElapsedMs(value)
	const days = Math.floor(elapsedMs / DAY_MS)
	const hours = Math.floor((elapsedMs % DAY_MS) / HOUR_MS)
	return `${days}d ${hours}h`
}

/**
 * due_date (YYYY-MM-DD) を「残り日数」表現にフォーマットする。
 * - 超過: "N日超過"
 * - 今日: "今日"
 * - 明日: "明日"
 * - 2日後以上: "N日後"
 */
export const formatDueDateLabel = (dueDate: string) => {
	const remaining = getRemainingDays(dueDate)
	if (remaining < 0) return `${-remaining}日超過`
	if (remaining === 0) return "今日"
	if (remaining === 1) return "明日"
	return `${remaining}日後`
}

export const formatSyncRelativeTime = (value?: string | null) => {
	if (!value) return "未同期"
	const elapsedMs = getElapsedMs(value)
	if (elapsedMs < 60 * 1000) return "たった今"
	if (elapsedMs < HOUR_MS) return `${Math.floor(elapsedMs / 60000)}分前`
	if (elapsedMs < DAY_MS) return `${Math.floor(elapsedMs / HOUR_MS)}時間前`
	return `${Math.floor(elapsedMs / DAY_MS)}日前`
}

export const normalizeComment = (comment: string) => {
	return comment.trim()
}

// Slack のメッセージ本文に含まれるリンクは `<https://example.com>` または
// `<https://example.com|表示名>` という形式で入ってくる。これを描画用の
// セグメント配列に分解する (JSX は呼び出し側で組み立てる)。
export type SlackTextSegment =
	| { type: "text"; value: string }
	| { type: "link"; url: string; label: string }

const SLACK_LINK_REGEX = /<(https?:\/\/[^|>]+)(?:\|([^>]*))?>/g

export const parseSlackText = (text: string): SlackTextSegment[] => {
	const segments: SlackTextSegment[] = []
	let lastIndex = 0
	for (const match of text.matchAll(SLACK_LINK_REGEX)) {
		const matchIndex = match.index ?? 0
		if (matchIndex > lastIndex) {
			segments.push({ type: "text", value: text.slice(lastIndex, matchIndex) })
		}
		const url = match[1]
		const label = match[2]?.trim() ? match[2] : url
		segments.push({ type: "link", url, label })
		lastIndex = matchIndex + match[0].length
	}
	if (lastIndex < text.length) {
		segments.push({ type: "text", value: text.slice(lastIndex) })
	}
	return segments
}

export const getErrorMessage = (error: unknown) =>
	error instanceof Error ? error.message : "unknown error"

export const isEditableElement = (target: EventTarget | null) => {
	if (!(target instanceof HTMLElement)) return false
	return (
		target.isContentEditable ||
		target.tagName === "INPUT" ||
		target.tagName === "TEXTAREA" ||
		target.tagName === "SELECT" ||
		target.tagName === "BUTTON"
	)
}

const SLACK_WORKSPACE_URL = "https://souhl-inc.slack.com"

export function buildSlackThreadUrl(
	channelId: string,
	threadTs: string
): string {
	const tsWithoutDot = threadTs.replace(".", "")
	return `${SLACK_WORKSPACE_URL}/archives/${channelId}/p${tsWithoutDot}`
}

// スレッド内の特定メッセージ(focusTs)にフォーカスする permalink を組み立てる。
// - PC: browser=1 を付けて Slack の interstitial をスキップし web で開く
// - モバイル: browser=1 を付けず、Slack アプリへの遷移 (universal link / interstitial) を許可する
export const buildSlackFocusUrl = (
	channelId: string,
	threadTs: string,
	focusTs: string,
	mobile: boolean
): string => {
	const base = `${buildSlackThreadUrl(channelId, focusTs)}?thread_ts=${threadTs}&cid=${channelId}`
	return mobile ? base : `${base}&browser=1`
}

export type SlackAppSetting = { enabled: boolean }

const SLACK_APP_STORAGE_KEY = "souhl_admin.taiga.slackApp"

const DEFAULT_SLACK_APP_SETTING: SlackAppSetting = {
	enabled: false,
}

export const loadSlackAppSetting = (): SlackAppSetting => {
	if (typeof window === "undefined") return DEFAULT_SLACK_APP_SETTING
	try {
		const raw = window.localStorage.getItem(SLACK_APP_STORAGE_KEY)
		if (!raw) return DEFAULT_SLACK_APP_SETTING
		const parsed = JSON.parse(raw) as Partial<SlackAppSetting>
		return {
			enabled:
				typeof parsed?.enabled === "boolean"
					? parsed.enabled
					: DEFAULT_SLACK_APP_SETTING.enabled,
		}
	} catch {
		return DEFAULT_SLACK_APP_SETTING
	}
}

export const saveSlackAppSetting = (setting: SlackAppSetting) => {
	try {
		window.localStorage.setItem(SLACK_APP_STORAGE_KEY, JSON.stringify(setting))
	} catch {
		// localStorage 書き込み失敗時はサイレントに無視（プライベートモード等）
	}
}

// cc.sh の実行元になる worktree のスロット番号 (refund-hub1 ~ refund-hub7)。
// ローカルには `refund-hubN` という名前で複数の clone / worktree を並べており、
// cc.sh 本体は `refund-hub1/.obsidian/scripts/cc.sh` にしか存在しない。
// Cursor で開いている workspace が refund-hub1 以外の場合、
// tasks.json は cwd 側 (=Cursor の workspace) に書かないと Run Task に出てこないので、
// `../refund-hub1/.obsidian/scripts/cc.sh {mode} --repo-root . ...` の形にする。
export const WORKTREE_SLOTS = ["1", "2", "3", "4", "5", "6", "7"] as const
export type WorktreeSlot = (typeof WORKTREE_SLOTS)[number]

const WORKTREE_SLOT_STORAGE_KEY = "souhl_admin.taiga.ccWorktreeSlot"
const DEFAULT_WORKTREE_SLOT: WorktreeSlot = "1"

export const loadWorktreeSlot = (): WorktreeSlot => {
	if (typeof window === "undefined") return DEFAULT_WORKTREE_SLOT
	try {
		const raw = window.localStorage.getItem(WORKTREE_SLOT_STORAGE_KEY)
		if (!raw) return DEFAULT_WORKTREE_SLOT
		return (WORKTREE_SLOTS as readonly string[]).includes(raw)
			? (raw as WorktreeSlot)
			: DEFAULT_WORKTREE_SLOT
	} catch {
		return DEFAULT_WORKTREE_SLOT
	}
}

export const saveWorktreeSlot = (slot: WorktreeSlot) => {
	try {
		window.localStorage.setItem(WORKTREE_SLOT_STORAGE_KEY, slot)
	} catch {
		// localStorage 書き込み失敗時はサイレントに無視（プライベートモード等）
	}
}

// cc.sh の引数形式: 'SESSION_ID:WORKTREE_PATH:NAME'
// NAME は任意文字を含みうるためシングルクォートで囲む (内部の ' は '\'' にエスケープ)
const quoteShellArg = (raw: string) => `'${raw.replaceAll("'", `'\\''`)}'`

export type CcSessionArg = {
	ccSessionId: string
	worktreePath: string | null
	name: string
}

export const buildCcCommand = (
	mode: "editor" | "panel",
	sessions: readonly CcSessionArg[],
	slot: WorktreeSlot
): string => {
	const args = sessions
		.map((s) =>
			quoteShellArg(`${s.ccSessionId}:${s.worktreePath ?? ""}:${s.name}`)
		)
		.join(" ")
	// slot=1 は cc.sh の所在地なのでそのまま。それ以外は refund-hub1 の script を借りて
	// --repo-root . で cwd (=Cursor workspace) の .vscode/tasks.json に書き込ませる。
	const script =
		slot === "1"
			? ".obsidian/scripts/cc.sh"
			: "../refund-hub1/.obsidian/scripts/cc.sh"
	const repoRootFlag = slot === "1" ? "" : " --repo-root ."
	return `${script} ${mode}${repoRootFlag} ${args}`
}

// 優先度・期限・送信者・メモ検索フィルタの永続化設定。
// 次に開いたときに前回の filter を引き継ぐため localStorage に保存する。
export type TaigaFilterSetting = {
	priority: PrioritySelectableValue[]
	dueDate: DueDateSelectableValue[]
	sender: SenderFilterValue
	session: SessionFilter
	search: SearchFilterValue
}

const TAIGA_FILTER_STORAGE_KEY = "souhl_admin.taiga.filter"

const DEFAULT_TAIGA_FILTER_SETTING: TaigaFilterSetting = {
	priority: [],
	dueDate: [],
	sender: null,
	session: DEFAULT_SESSION_FILTER,
	search: "",
}

// 既知の選択肢のみ許可するための集合。option が将来増減しても
// 古い保存値で壊れないよう、load 時にここでバリデーションする。
const VALID_PRIORITY_VALUES = new Set<PrioritySelectableValue>(
	PRIORITY_FILTER_OPTIONS.map((o) => o.value).filter(
		(v): v is PrioritySelectableValue => v !== null
	)
)
const VALID_DUE_DATE_VALUES = new Set<DueDateSelectableValue>(
	DUE_DATE_FILTER_OPTIONS.map((o) => o.value).filter(
		(v): v is DueDateSelectableValue => v !== null
	)
)

export const loadTaigaFilterSetting = (): TaigaFilterSetting => {
	if (typeof window === "undefined") return DEFAULT_TAIGA_FILTER_SETTING
	try {
		const raw = window.localStorage.getItem(TAIGA_FILTER_STORAGE_KEY)
		if (!raw) return DEFAULT_TAIGA_FILTER_SETTING
		const parsed = JSON.parse(raw) as Partial<TaigaFilterSetting>
		const priority = Array.isArray(parsed?.priority)
			? parsed.priority.filter((v) => VALID_PRIORITY_VALUES.has(v))
			: DEFAULT_TAIGA_FILTER_SETTING.priority
		const dueDate = Array.isArray(parsed?.dueDate)
			? parsed.dueDate.filter((v) => VALID_DUE_DATE_VALUES.has(v))
			: DEFAULT_TAIGA_FILTER_SETTING.dueDate
		// sender は複数選択 (string[] | null)。旧形式の単一選択 (string) が保存されている
		// 場合は「その 1 人だけ選択した状態」として移行する。
		const sender = Array.isArray(parsed?.sender)
			? parsed.sender.filter((v): v is string => typeof v === "string")
			: typeof parsed?.sender === "string"
				? [parsed.sender]
				: DEFAULT_TAIGA_FILTER_SETTING.sender
		// session は busy/idle 両方が boolean のときだけ採用し、壊れた値はデフォルトに倒す。
		// rc は後から追加されたフィールドのため、古い保存値 (未定義・旧 rcExcluded boolean 形式)
		// は移行する: rcExcluded=true → "nonRc"、それ以外 → "all"。
		const legacyRcExcluded = (
			parsed?.session as { rcExcluded?: unknown } | undefined
		)?.rcExcluded
		const session =
			parsed?.session &&
			typeof parsed.session === "object" &&
			typeof parsed.session.busy === "boolean" &&
			typeof parsed.session.idle === "boolean"
				? {
						busy: parsed.session.busy,
						idle: parsed.session.idle,
						rc: RC_FILTER_OPTIONS.some((o) => o.value === parsed.session?.rc)
							? (parsed.session.rc as RcFilterValue)
							: legacyRcExcluded === true
								? ("nonRc" as const)
								: ("all" as const),
					}
				: DEFAULT_TAIGA_FILTER_SETTING.session
		const search =
			typeof parsed?.search === "string"
				? parsed.search
				: DEFAULT_TAIGA_FILTER_SETTING.search
		return { priority, dueDate, sender, session, search }
	} catch {
		return DEFAULT_TAIGA_FILTER_SETTING
	}
}

// 変更したフィールドだけ上書きして書き戻す（read-modify-write）。
// 複数 window で別々のフィールドを変更したときに、片方の変更が
// もう片方の丸ごと上書きで消えるのを防ぐ。
export const saveTaigaFilterSetting = (patch: Partial<TaigaFilterSetting>) => {
	try {
		const next: TaigaFilterSetting = { ...loadTaigaFilterSetting(), ...patch }
		window.localStorage.setItem(TAIGA_FILTER_STORAGE_KEY, JSON.stringify(next))
	} catch {
		// localStorage 書き込み失敗時はサイレントに無視（プライベートモード等）
	}
}

// テキストをクリップボードへコピーする。
// Cursor/VS Code の webview 拡張 (taiga-browser) 内の iframe では navigator.clipboard が
// permissions policy でブロックされ reject されるため、その場合は親フレームへ
// postMessage して拡張側の vscode.env.clipboard で書き込む。通常ブラウザでは従来通り。
export const copyTextToClipboard = async (text: string): Promise<void> => {
	// taiga-browser 拡張の webview iframe では navigator.clipboard.writeText が
	// 「成功扱いで resolve するのに OS のクリップボードへは書き込まれない」ことがあり、
	// reject を待つ fallback では検知できない。そのため iframe 埋め込み時は常に
	// 親フレームへも postMessage して拡張側 (vscode.env.clipboard) で書き込む。
	// Simple Browser 等の受け手がいない環境では postMessage は無視され、
	// navigator.clipboard 側の書き込みが効くので両送りで問題ない。
	if (window.parent !== window) {
		window.parent.postMessage({ type: "taiga.copyToClipboard", text }, "*")
	}
	try {
		await navigator.clipboard.writeText(text)
	} catch (err) {
		if (window.parent !== window) return
		throw err
	}
}

// フォーカス中の入力欄 (input / textarea / contentEditable) へテキストを挿入する。
// React の controlled input に反映させるため、native の value setter + input イベントを使う。
const insertTextToActiveElement = (text: string) => {
	const el = document.activeElement
	if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
		const start = el.selectionStart ?? el.value.length
		const end = el.selectionEnd ?? el.value.length
		const proto =
			el instanceof HTMLInputElement
				? window.HTMLInputElement.prototype
				: window.HTMLTextAreaElement.prototype
		const setter = Object.getOwnPropertyDescriptor(proto, "value")?.set
		const next = el.value.slice(0, start) + text + el.value.slice(end)
		if (setter) setter.call(el, next)
		else el.value = next
		const caret = start + text.length
		el.setSelectionRange(caret, caret)
		el.dispatchEvent(new Event("input", { bubbles: true }))
		return
	}
	if (el instanceof HTMLElement && el.isContentEditable) {
		document.execCommand("insertText", false, text)
	}
}

// フォーカス中の入力欄・選択範囲から選択テキストを取得する。
const getSelectedText = (): string => {
	const sel = window.getSelection()?.toString() ?? ""
	if (sel) return sel
	const el = document.activeElement
	if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
		const start = el.selectionStart ?? 0
		const end = el.selectionEnd ?? 0
		if (end > start) return el.value.slice(start, end)
	}
	return ""
}

// taiga-browser 拡張 (Cursor webview) の iframe 内では Cmd+V / Cmd+C のネイティブ動作が
// clipboard 権限で reject され (書き込みは silent 失敗、読み取りは reject)、
// キーバインドが効かないため、iframe 埋め込み時は capture 段階で intercept して
// 親フレーム経由 (拡張の vscode.env.clipboard) で読み書きする。
// - Cmd+V: 親へ paste 要求 → 返ってきた text を activeElement に挿入
// - Cmd+C: 選択テキストを親経由でクリップボードに書き込み
// 通常ブラウザ (非 iframe) では何もしない。cleanup 関数を返す。
export const setupEmbeddedClipboardBridge = (): (() => void) | undefined => {
	if (typeof window === "undefined" || window.parent === window)
		return undefined
	const handleKeyDown = (e: KeyboardEvent) => {
		if (!(e.metaKey || e.ctrlKey)) return
		const key = e.key.toLowerCase()
		if (key === "v") {
			e.preventDefault()
			window.parent.postMessage({ type: "taiga.requestPaste" }, "*")
			return
		}
		if (key === "c") {
			const text = getSelectedText()
			if (!text) return
			e.preventDefault()
			window.parent.postMessage({ type: "taiga.copyToClipboard", text }, "*")
		}
	}
	const handleMessage = (e: MessageEvent) => {
		const data: unknown = e.data
		if (
			typeof data !== "object" ||
			data === null ||
			(data as { type?: unknown }).type !== "taiga.pasteText" ||
			typeof (data as { text?: unknown }).text !== "string"
		) {
			return
		}
		insertTextToActiveElement((data as { text: string }).text)
	}
	// React 側で stopPropagation されると bubble 段階では拾えないため capture で。
	document.addEventListener("keydown", handleKeyDown, true)
	window.addEventListener("message", handleMessage)
	return () => {
		document.removeEventListener("keydown", handleKeyDown, true)
		window.removeEventListener("message", handleMessage)
	}
}

// souhl-inc workspace の team ID。slack:// deep link のルーティングに必要。
const SLACK_TEAM_ID = "T064YB1PGLF"

// Slack アプリの deep link を組み立てる。
// ブラウザの permalink (archives/p{messageTs}?thread_ts={threadTs}) と同様に、
// 「どのスレッドの」「どのメッセージか」を両方指定する。message のみだとスレッド内の
// reply はメインタイムラインに存在しないためアプリが解決できず、最新メッセージに
// 飛べないことがあるので thread_ts も必ず付ける。
const buildSlackDeepLink = (
	channelId: string,
	threadTs: string,
	messageTs: string
): string =>
	`slack://channel?team=${SLACK_TEAM_ID}&id=${channelId}&message=${messageTs}&thread_ts=${threadTs}`

// Slack スレッドを開く。
// - threadTs: スレッド親メッセージの ts (どのスレッドか)
// - messageTs: スレッド内でフォーカスするメッセージの ts (どのメッセージか。通常は最新)
// - モバイル: slack:// deep link の message/thread_ts パラメータは undocumented で
//   モバイルアプリでは無視され最新メッセージに飛べないため、deep link は使わず
//   https permalink (browser=1 なし) を現在のタブで開く。Slack の interstitial /
//   universal link 経由でアプリが開いても、URL が最新メッセージを指しているので
//   PC の web と同じく最新メッセージにフォーカスされる。
// - PC で「Slackアプリで開く」有効時: slack:// deep link を location に渡す。カスタム URL
//   スキームのため OS が Slack アプリへ受け渡すだけで、現在のページ(Taiga)は遷移せず
//   ブラウザのタブも一切開かない。
// - それ以外 (PC・無効時): 従来通りブラウザの別タブで開く (url は呼び出し側が組み立てた permalink)。
export const openSlackThread = (
	url: string,
	channelId: string,
	threadTs: string,
	messageTs: string,
	slackApp: SlackAppSetting,
	isMobile: boolean
) => {
	if (typeof window === "undefined") return
	if (isMobile) {
		window.location.href = url
		return
	}
	if (slackApp.enabled) {
		const deepLink = buildSlackDeepLink(channelId, threadTs, messageTs)
		openDeepLink(deepLink)
		return
	}
	window.open(url, "_blank", "noopener,noreferrer")
}

// カスタム URL スキーム (slack:// / claude:// 等) の deep link を開く。
// - Cursor/VS Code の webview 拡張 (taiga-browser) 内の iframe で動いている場合、
//   iframe 自身をカスタムスキームへ location 遷移させると webview がブラックアウトする
//   (Chromium が iframe をカスタムスキームへナビゲートし、その iframe が死ぬ)。
//   拡張側 (extension.js) が postMessage を受けて vscode.env.openExternal で
//   OS へ渡すので、iframe 内では location.href を触らない。
// - それ以外 (通常ブラウザ・モバイル) は location.href で OS のアプリ受け渡しに任せる。
//   カスタムスキームのため現在のページ (Taiga) は遷移しない。
const openDeepLink = (deepLink: string) => {
	if (typeof window === "undefined") return
	if (window.parent !== window) {
		window.parent.postMessage(
			{ type: "taiga.openExternal", url: deepLink },
			"*"
		)
		return
	}
	window.location.href = deepLink
}

// Claude アプリ (デスクトップ / モバイル) でセッションを開く deep link。
// {session-id} は cc-server のセッション id (= claude -p の session id)。
// アプリのセッション一覧に出るのは Remote Control 有効セッションのみなので、
// RC でないセッションは Slack スレッドで「/rc fable」を送って有効化してから開く。
// NOTE: この id は claude.ai 側では解決できないため、アプリが起動するだけで
// 該当セッションに focus はされない。focus させたい場合は claudeRemoteControlSessionId
// (claude.ai の会話ページ URL) を使う。
const buildClaudeSessionDeepLink = (ccSessionId: string): string =>
	`claude://code/${ccSessionId}`

// claude.ai の会話ページ URL。claudeRemoteControlSessionId は cc-server が bridge session
// (claude.ai の sessions API) から解決した `session_01...` 形式の ID。
const buildClaudeSessionWebUrl = (
	claudeRemoteControlSessionId: string
): string => `https://claude.ai/code/${claudeRemoteControlSessionId}`

// https URL を新しいタブ / OS 既定ブラウザで開く。
// webview 拡張 (taiga-browser) 内の iframe では window.open が効かないことが
// あるため、deep link と同じ postMessage 経路 (taiga.openExternal) で OS に渡す。
const openExternalUrl = (url: string) => {
	if (typeof window === "undefined") return
	if (window.parent !== window) {
		window.parent.postMessage({ type: "taiga.openExternal", url }, "*")
		return
	}
	window.open(url, "_blank", "noopener,noreferrer")
}

export const openClaudeSession = (
	claudeRemoteControlSessionId: string | null,
	ccSessionId: string
) => {
	// claude.ai セッション ID が解決済みなら会話ページを直接開く（該当セッションが開く）。
	// 未解決（cc-server の bridge sweep 反映前）はアプリを開くだけの deep link に
	// フォールバックする（アプリは起動するが focus はされない）。
	if (claudeRemoteControlSessionId) {
		openExternalUrl(buildClaudeSessionWebUrl(claudeRemoteControlSessionId))
		return
	}
	openDeepLink(buildClaudeSessionDeepLink(ccSessionId))
}
